import { StrictMode, useCallback, useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { resolveSignature, forceResolveSignature } from '../lib/signature-resolver';
import { clearTokenCache } from '../lib/auth';
import ambientLogo from '../../assets/ambient-logo.svg';

interface SignatureStatus {
  templateId: string;
  hash: string;
  htmlPreview: string;
  fetchedAt: number;
}

function App() {
  const [status, setStatus] = useState<SignatureStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const sig = await resolveSignature();
      if (sig) {
        setStatus({
          templateId: sig.templateId,
          hash: sig.hash,
          htmlPreview: sig.renderedHtml,
          fetchedAt: sig.fetchedAt,
        });
      } else {
        setStatus(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load signature');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    Office.onReady(() => {
      setUserEmail(Office.context.mailbox.userProfile.emailAddress);
      fetchStatus();
    });
  }, [fetchStatus]);

  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const sig = await forceResolveSignature();
      if (sig) {
        setStatus({
          templateId: sig.templateId,
          hash: sig.hash,
          htmlPreview: sig.renderedHtml,
          fetchedAt: sig.fetchedAt,
        });
      } else {
        setStatus(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh signature');
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => {
    void clearTokenCache();
    setStatus(null);
    setError('Signed out. Click Sign In to authenticate again.');
  };

  const handleSignIn = () => {
    setError(null);
    fetchStatus();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <img src={ambientLogo} alt="Ambient by Ziviso" style={{ height: 40 }} />
      </div>

      <div
        style={{
          padding: '8px 12px',
          background: '#e2e8f0',
          borderRadius: 6,
          fontSize: 13,
          color: '#475569',
        }}
      >
        {userEmail || 'Loading...'}
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 24, color: '#64748b' }}>
          Loading signature...
        </div>
      )}

      {error && (
        <div
          style={{
            padding: '8px 12px',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 6,
            color: '#dc2626',
            fontSize: 13,
          }}
        >
          {error}
          {error.includes('Signed out') && (
            <button
              type="button"
              onClick={handleSignIn}
              disabled={loading}
              style={{
                display: 'block',
                marginTop: 8,
                padding: '6px 12px',
                background: '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 500,
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          )}
        </div>
      )}

      {!loading && !error && status && (
        <>
          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>
              STATUS
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 13,
              }}
            >
              <div
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  background: '#22c55e',
                }}
              />
              Active
              <span style={{ color: '#94a3b8', marginLeft: 'auto' }}>
                {new Date(status.fetchedAt).toLocaleTimeString()}
              </span>
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>
              SIGNATURE PREVIEW
            </div>
            <div
              style={{
                border: '1px solid #e2e8f0',
                borderRadius: 6,
                padding: 12,
                background: 'white',
                maxHeight: 240,
                overflow: 'auto',
              }}
              dangerouslySetInnerHTML={{ __html: status.htmlPreview }}
            />
          </div>
        </>
      )}

      {!loading && !error && !status && (
        <div
          style={{
            textAlign: 'center',
            padding: 24,
            color: '#94a3b8',
            fontSize: 13,
          }}
        >
          No signature configured for your account.
          <br />
          Contact your IT administrator.
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={loading}
          style={{
            flex: 1,
            padding: '8px 12px',
            border: '1px solid #cbd5e1',
            borderRadius: 6,
            background: 'white',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          {loading ? 'Refreshing...' : 'Refresh Signature'}
        </button>
        <button
          type="button"
          onClick={handleSignOut}
          style={{
            padding: '8px 12px',
            border: '1px solid #cbd5e1',
            borderRadius: 6,
            background: 'white',
            cursor: 'pointer',
            fontSize: 13,
            color: '#dc2626',
          }}
        >
          Sign Out
        </button>
      </div>

      <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center' }}>
        Managed by your organization via Ambient by Ziviso
      </div>
    </div>
  );
}

Office.onReady(() => {
  const container = document.getElementById('root');
  if (container) {
    createRoot(container).render(
      <StrictMode>
        <App />
      </StrictMode>,
    );
  }
});
